import { memo, useRef, useState } from 'react';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  onSubmit: (body: string, files: File[]) => Promise<void>;
  onError: (msg: string) => void;
}

export const CommentForm = memo(function CommentForm({ onSubmit, onError }: Props): JSX.Element {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    for (const f of picked) {
      if (!ALLOWED_TYPES.includes(f.type)) { onError(`${f.name}: type not allowed`); continue; }
      if (f.size > MAX_BYTES) { onError(`${f.name}: exceeds 10 MB`); continue; }
      setFiles((prev) => [...prev, f]);
    }
  }

  async function handleSubmit() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onSubmit(body.trim(), files);
      setBody('');
      setFiles([]);
    } catch {
      // error surfaced by parent via onError
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pt-2 border-t border-gray-800 space-y-2">
      <textarea
        className="input w-full min-h-[80px]"
        placeholder="Add a comment…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={5000}
        disabled={busy}
      />
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-xs bg-gray-800 rounded px-2 py-1">
              <span className="flex-1 truncate text-gray-200">{f.name}</span>
              <span className="text-gray-500">{(f.size / 1024).toFixed(0)} KB</span>
              <button className="text-red-400 hover:underline" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <button className="btn-primary" disabled={busy || !body.trim()} onClick={handleSubmit}>
          {busy ? 'Posting…' : 'Post comment'}
        </button>
        <button className="btn-secondary text-sm" type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
          Attach file
        </button>
        <input ref={fileRef} type="file" className="hidden" multiple accept={ALLOWED_TYPES.join(',')} onChange={onFileChange} />
      </div>
    </div>
  );
});
